import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Button } from '../../platform/primitives/Button'
import { HeroTile } from '../../platform/primitives/Panel'
import { InstitutionAvatar } from '../../platform/primitives/InstitutionAvatar'
import { Money } from '../../platform/primitives/Money'
import { accountHealth, type Health } from './accountHealth'
import { groupOf } from './accountGroups'
import { signedCents } from './accountMoney'
import { formatRelative } from './accountsStaleLabels'
import { currentMonthRange, useAccountSummary } from './useAccountSummary'
import type { Account } from './useAccounts'

/**
 * The account detail hero — M5 Ticket 4 (#425 B2). Monogram, name,
 * `Institution · ••mask`, a sync-freshness pill, the balance (cents split —
 * already ships, see `Money.tsx`), and a stats row (Available · In {month}).
 *
 * `role="group" aria-label={account.name}` is asserted directly by
 * `account-detail.spec.ts` (`page.getByRole('group', { name: … })`) — kept
 * unchanged from `AccountDetail.tsx`'s own prior inline markup, now the root
 * this component returns instead.
 */

/**
 * B2's "Synced 4 min ago" pill — one visual treatment regardless of health
 * (`--on-accent` fill, `--accent` text, both opaque — the SAME 12.65:1 dark /
 * 12.05:1 light ratio `Money.tsx`'s own docblock already measures for the
 * un-inverted pairing, contrast is symmetric in which side is foreground),
 * with its TEXT carrying the per-state difference rule 11 asks for.
 *
 * A dedicated formatter below, not `statusInfoOf` (`connectionStatus.tsx`)
 * reused a second time — that mapping is deliberately terse ("Synced" /
 * "Stale" / "Reconnect" / "Manual") because its own callers (`AccountCard`,
 * `InstitutionsPanel`) are dense rows where a short categorical word is the
 * whole budget. The hero is roomy and singular, so the actual freshness —
 * "4 min ago" vs. "2d ago" — is more informative than repeating a category
 * word, and it still differs per state because the underlying instant does.
 * Still reads `accountHealth()` alone, never a second interpretation of the
 * account's state — only a second RENDERING of the same `Health` value, the
 * split `Icon.tsx`'s own docblock draws between "which surface a component
 * IS" and "what it looks like".
 *
 * **`'Reconnect needed'`, not the bare imperative `'Reconnect'`** (architect
 * review, #425 T4 gate, blocking 2). The pill carries button-shaped chrome —
 * pill radius, `--on-accent` fill, `font-weight: 600` — on a non-interactive
 * `<span>`, and there is no Reconnect CONTROL anywhere on this screen:
 * `HealthNote` (`AccountDetail.tsx`) is prose, and the only buttons are Sync
 * now and Hide account. The bare imperative was chosen to dodge
 * `account-detail.spec.ts`'s single-match `/needs reconnecting/i` (`HealthNote`
 * already renders "{institution} needs reconnecting." for the same account) —
 * the right constraint, reached with the wrong label. `'Reconnect needed'`
 * stays clear of that regex while reading as a STATE rather than an
 * instruction, matching the other three branches. If the pill should
 * genuinely become the reauth entry point, that is Ticket 5's decision, not
 * a label change here.
 */
function heroSyncLabel(health: Health): string {
  switch (health.kind) {
    case 'manual':
      return 'Updated by you'
    case 'ok':
      return health.lastSyncedAt ? `Synced ${formatRelative(health.lastSyncedAt)}` : 'Synced'
    case 'stale':
      return `Last synced ${formatRelative(health.since)}`
    case 'reauth':
      return 'Reconnect needed'
    case 'disconnected':
      return 'Disconnected'
  }
}

const IN_MONTH_SKELETON = <Skeleton height={20} width={64} />

/**
 * "In {month}" — #425 D1, buildable today via `/transactions/summary`.
 * `kind="stock"`, deliberately NOT `kind="flow"`: `Money.tsx`'s flow branch
 * sets an explicit inline `color: var(--ink)` for a non-positive figure on
 * BOTH its variants (documented there as "the byte-identical behaviour this
 * call site already had", checked against every flow call site in the repo —
 * none of them a `HeroTile`). A net outflow month (`netCents <= 0`) is the
 * ordinary case, not the exception, and rendering it on lime with a hardcoded
 * `--ink` is the identical 1:1-in-dark defect `Money.tsx`'s own stock fix
 * already retired — reintroduced one call site over. `stock` inherits
 * (`--control-ink`), which resolves correctly on this surface and every
 * other, the same choice the pre-existing "Available" figure already makes.
 *
 * **The error branch is handled BEFORE `<Async>`, not through it** (architect
 * review, #425 T4 gate, blocking 1). `<Async>` unconditionally wraps every
 * branch in a block `<div aria-busy>` (`Async.tsx:320`, deliberate — no
 * `display: contents`, issue 264) and its error branch is a fixed
 * `<AlertPanel>` (`--neg-soft` background, `--neg` text). `--neg-soft`
 * composited over `--accent` measures **1.47:1 in dark** (measured for this
 * fix; nowhere near the 4.5 floor) — the same family of defect `Money.tsx`'s
 * own docblock records fixing for the hero balance, reached here through a
 * different token — and a full `AlertPanel` plus a "Try again" button is
 * also the wrong SHAPE for a small stat's failure on a lime tile,
 * independent of the colour question. (`.account-hero__stat` now stacks its
 * children vertically regardless — #425's design gap-fill pass, 2026-09-17 —
 * so the two-line split B-1 originally fixed is the artboard's own intended
 * layout; this docblock's colour/shape reasoning for bypassing `<Async>`'s
 * error branch is unaffected by that later change.)
 *
 * So: loading/empty/success still route through `<Async>` normally (rule 10
 * — every state the surface can report is still handled, and `state` is
 * narrowed to exclude `'error'` by the early return, so `<Async>` never has
 * to). Error renders a plain, inline message instead.
 *
 * **Renders `state.recovery`, not just `state.message`** (team-lead re-gate,
 * #425 T4: "render it"). `useAccountSummary.ts` sets `recovery: { label:
 * 'Try again', onAct: () => query.refetch() }` for exactly this branch, and
 * dropping `<Async>`'s own rendering without also rendering that field would
 * leave it dead — `docs/rules.md`'s own line, "Error says whose fault and
 * the recovery path", and the exact defect `Household.tsx:436-453` already
 * fixed once (issue 311) for the identical shape: a query's own error state
 * hand-rolled outside `<Async>` because the screen already has one, with the
 * SAME `size="panel" variant="outline"` button that file uses. No new
 * contrast guard needed for the button either: `.btn--outline`'s boundary on
 * `.hero-tile` resolves to `--on-accent-2`, already pinned at 5.74/5.43 in
 * `compositeContrast.test.tsx`.
 *
 * **The message itself is full-strength `--control-ink`, not the dim rung.**
 * `.account-hero__stat`'s ambient colour is `--control-ink-dim` — right for
 * a LABEL, wrong for a failure: text that reads exactly like a caption is a
 * failure nobody notices. `.account-hero__stat-error` overrides it back to
 * `--control-ink`, the SAME `--on-accent`/`--ink` pairing the balance and the
 * pill already use — again no new guard needed, since that pairing is the
 * 12.65/12.05 ratio `Money.tsx`'s own docblock already measures.
 *
 * `role="alert"` keeps the announcement `AlertPanel` would have given a
 * screen-reader user, without its visual weight or its unmeasured
 * `--neg-soft`-on-`--accent` colour (1.47:1 in dark, measured — see above).
 *
 * This is #431's third caller and the first where neither existing
 * workaround (`OwnerPicker`'s content-beside-a-div — retired, design
 * gap-fill round 6, by `OwnerMenu` — and `InstitutionsPanel`'s
 * effective-state reframing) applied — recorded there as the case that
 * raised its priority, not solved by it.
 */
function InMonthStat({ account }: { account: Account }) {
  const { start, end, label } = currentMonthRange()
  const state = useAccountSummary(account.id, start, end)

  if (state.status === 'error') {
    return (
      <div className="account-hero__stat">
        <span className="account-hero__stat-label">{`In ${label}`}</span>
        <span className="account-hero__stat-error" role="alert">
          {state.message}
        </span>
        {state.recovery && (
          <Button size="panel" variant="outline" onClick={state.recovery.onAct}>
            {state.recovery.label}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="account-hero__stat">
      <span className="account-hero__stat-label">{`In ${label}`}</span>
      <Async
        surface="account-summary"
        state={state}
        skeleton={IN_MONTH_SKELETON}
        // Unreachable — `useAccountSummary` passes no `isEmpty`, a summary
        // always has a shape. Required anyway, `Async`'s own contract: the
        // prop stays so a future caller that DOES filter cannot forget it.
        empty={{ title: 'No activity this month.' }}
      >
        {(summary) => (
          <span className="account-hero__stat-value">
            <Money cents={summary.netCents} currency={account.currency} kind="stock" />
          </span>
        )}
      </Async>
    </div>
  )
}

export function AccountHero({ account }: { account: Account }) {
  const group = groupOf(account.type)
  const cents = account.currentCents === null ? null : signedCents(account.currentCents, group)
  const health = accountHealth(account)
  const metaLine = [account.institutionName, account.mask ? `••${account.mask}` : null]
    .filter(Boolean)
    .join(' · ')

  return (
    <HeroTile role="group" aria-label={account.name}>
      <div className="account-hero__identity">
        {/* `logoUrl={null}` always: `Account` (`useAccounts.ts`) carries no
            logo field at all — only `ProviderItem` does — so this always
            renders the fallback initials, the same choice `AccountCard.tsx`'s
            identical monogram already makes for the identical reason.
            `size="detail"` (`InstitutionAvatar.tsx`) carries the 40px box,
            its matching font size, AND the surface-relative ink together —
            see that component's own docblock for why a route-level override
            (this file's previous approach) could not. */}
        <InstitutionAvatar
          name={account.institutionName ?? account.name}
          logoUrl={null}
          size="detail"
        />
        <span className="account-hero__identity-text">
          {/* `h2`, not `h1` — `ChromeLayout`'s breadcrumb owns the
              document's only `h1` (`CategoryDetail.tsx`'s own precedent).
              Contains ONLY the account name: `account-detail.spec.ts`
              queries `getByRole('heading', { level: 2, name: account.name
              })` with an EXACT name, so the sync pill sits as a SIBLING,
              never a descendant, of this heading. */}
          <h2 className="account-hero__name">{account.name}</h2>
          {metaLine && <span className="account-hero__meta">{metaLine}</span>}
        </span>
        {/* Pushed to the tile's own far edge (design gap-fill, 2026-09-17) —
            `margin-left: auto` on the pill itself rather than `justify-
            content: space-between` on `.account-hero__identity`: the avatar
            and the identity text must stay a tight pair on the left (their
            own `gap: 10px`), and an auto margin on the LAST item is what
            reserves all remaining row space before it regardless of how many
            siblings precede it, where `space-between` would only do the
            right thing for exactly two children. */}
        <span className="account-hero__pill">{heroSyncLabel(health)}</span>
      </div>

      {cents !== null && <Money cents={cents} currency={account.currency} variant="hero" kind="stock" />}

      <div className="account-hero__stats">
        {account.availableCents !== null && (
          <span className="account-hero__stat">
            {/* The artboard stacks label above value (design gap-fill,
                2026-09-17) — a correction to B-1's own inline fix, which
                matched Available to In-month's WRONG layout rather than the
                other way round. The `{' '}` between the label and the value
                is load-bearing, not stray formatting: `account-detail
                .spec.ts` locates this stat by the concatenated substring
                "Available $X", and flexbox's own whitespace-collapsing rule
                (a pure-whitespace text run generates no anonymous flex item)
                means this space survives in the accessible text without
                adding a visible blank row between the two stacked lines. */}
            <span className="account-hero__stat-label">Available</span>{' '}
            <span className="account-hero__stat-value">
              <Money cents={account.availableCents} currency={account.currency} kind="stock" />
            </span>
          </span>
        )}
        {/* No Pending line — `AccountResponse` carries no pending balance
            field at all, and the figure is dropped at ingest rather than
            merely unexposed (backend#356, #425 D3). Absent, not a fake
            `$0.00`. */}
        <InMonthStat account={account} />
      </div>
      {/* The exclusion caveat that used to sit here was removed by user
          decision (design gap-fill round 4, 2026-09-17) — it hedged a
          comparison nobody could make on this tile, was the hero's own
          longest line, and named only one of two stats. The fact survives
          as a code comment on `useAccountSummary.ts`, for whoever builds
          M6's transaction-list comparison. */}
    </HeroTile>
  )
}
